import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Button,
  useTheme,
  useMediaQuery,
  Avatar,
  Chip,
  alpha,
  Tabs,
  Tab,
} from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import ChatArea from "./ChatArea";
import ScoreboardArea from "./ScoreboardArea";
import CountdownTimer from "../CountdownTimer";
import { fireConfetti } from "../../utils/confetti";
import VoiceChat from "./VoiceChat";
import useSound from "use-sound";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import PauseCircleOutlineIcon from "@mui/icons-material/PauseCircleOutline";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";

const MANTRI_GUESS_TIME = 120;

function GameArea({ roomCode, player, roomState, socket, setSystemMessage, showAlert }) {
  const [mantriTimer, setMantriTimer] = useState(MANTRI_GUESS_TIME);
  const [showMantriGuessPrompt, setShowMantriGuessPrompt] = useState(false);
  const timerRef = useRef(null);
  const [roundResult, setRoundResult] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [mobileTab, setMobileTab] = useState(0);

  const [gamePaused, setGamePaused] = useState(false);
  const [pauseReason, setPauseReason] = useState("");

  const [playClick] = useSound("/sounds/click.mp3");
  const [playTick] = useSound("/sounds/tick.mp3");
  const [playSuccess] = useSound("/sounds/success.mp3");
  const [playFail] = useSound("/sounds/fail.mp3");

  const getCurrentRole = () => {
    if (!roomState?.players || !player?.id) return null;
    const livePlayer = roomState.players.find((p) => p.id === player.id);
    return livePlayer?.role || player.role || null;
  };

  useEffect(() => {
    return () => { if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; } };
  }, []);

  useEffect(() => {
    socket.on("gamePaused", ({ reason, remainingPlayers }) => {
      setGamePaused(true);
      setPauseReason(`${reason} (${remainingPlayers}/4 players connected)`);
      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    });
    socket.on("gameResumed", () => { setGamePaused(false); setPauseReason(""); });
    socket.on("syncTimer", ({ remainingTime }) => {
      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
      setMantriTimer(remainingTime);
      if (remainingTime > 0) {
        timerRef.current = setInterval(() => {
          setMantriTimer((prev) => {
            if (prev <= 5 && prev > 0) playTick();
            if (prev <= 1) { clearInterval(timerRef.current); timerRef.current = null; return 0; }
            return prev - 1;
          });
        }, 1000);
      }
    });
    socket.on("gameStarted", () => { setShowMantriGuessPrompt(false); setRoundResult(null); });
    socket.on("startMantriGuessTimer", ({ duration }) => {
      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
      const d = duration || MANTRI_GUESS_TIME;
      setMantriTimer(d);
      timerRef.current = setInterval(() => {
        setMantriTimer((prev) => {
          if (prev <= 1) { clearInterval(timerRef.current); timerRef.current = null; return 0; }
          return prev - 1;
        });
      }, 1000);
    });
    socket.on("mantriGuessMade", () => {
      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
      setShowMantriGuessPrompt(false);
      setMantriTimer(0);
    });
    socket.on("mantriTimeOut", () => {
      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
      setShowMantriGuessPrompt(false);
      setMantriTimer(0);
    });
    socket.on("roundResult", ({ rolesAssigned, mantriGuess, finalScores, chorUsername }) => {
      setRoundResult({ rolesAssigned, mantriGuess, finalScores, chorUsername });
      setShowMantriGuessPrompt(false);
    });

    return () => {
      ["gameStarted", "yourRole", "startMantriGuessTimer", "mantriGuessMade", "mantriTimeOut", "roundResult", "promptNextRound", "gamePaused", "gameResumed", "syncTimer"]
        .forEach((e) => socket.off(e));
      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    };
  }, [socket, player, setSystemMessage]);

  const handleMantriGuess = (guessedPlayerId) => {
    setShowMantriGuessPrompt(false);
    socket.emit("mantriGuess", { roomCode, guessedPlayerId }, (response) => {
      if (response && !response.success) {
        showAlert("Guess Failed", response.error || "Guess failed.", "error");
        setShowMantriGuessPrompt(true);
      }
    });
  };

  const handleNextRound = () => socket.emit("nextRound", { roomCode });
  const handleEndGame = () => {
    showAlert("End Game", "Are you sure you want to end the game?", "warning", () => socket.emit("endGame", { roomCode }));
  };

  useEffect(() => {
    if (roundResult?.mantriGuess?.isCorrect) { playSuccess(); fireConfetti(); }
    else if (roundResult?.mantriGuess?.hasGuessed === false || roundResult?.mantriGuess?.isCorrect === false) { playFail(); }
  }, [roundResult, playSuccess, playFail]);

  if (!roomState || !player) {
    return (
      <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Typography variant="h6" sx={{ opacity: 0.5 }}>Loading game...</Typography>
      </Box>
    );
  }

  const isMantriGuessPhase = roomState.status === "playing" && roomState.mantriGuess && !roomState.mantriGuess.hasGuessed;
  const currentRole = getCurrentRole();

  // Paused overlay
  if (gamePaused) {
    return (
      <Box
        sx={{
          position: "fixed", inset: 0, zIndex: 9999,
          display: "flex", flexDirection: "column",
          alignItems: "center", justifyContent: "center",
          backgroundColor: alpha("#000", 0.8),
          backdropFilter: "blur(10px)",
          color: "#fff",
          p: 3,
          textAlign: "center",
        }}
      >
        <motion.div animate={{ scale: [1, 1.08, 1] }} transition={{ repeat: Infinity, duration: 2 }} style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
          <PauseCircleOutlineIcon sx={{ fontSize: 64, mb: 1, opacity: 0.8 }} />
          <Typography variant="h4" sx={{ mb: 1, fontWeight: 700 }}>Game Paused</Typography>
        </motion.div>
        <Typography variant="body1" sx={{ opacity: 0.75, mt: 1, maxWidth: 340 }}>{pauseReason}</Typography>
        <Typography variant="body2" sx={{ opacity: 0.5, mt: 0.5 }}>Waiting for players to reconnect...</Typography>

        <Box sx={{ mt: 4, display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5 }}>
          {/* Host can end the game if a player never returns */}
          {player?.isHost && (
            <Button
              variant="contained"
              color="error"
              onClick={() => socket.emit("endGame", { roomCode })}
              sx={{ px: 4 }}
            >
              End Game
            </Button>
          )}
          {/* Any player can leave */}
          <Button
            variant="outlined"
            sx={{ color: "#fff", borderColor: "rgba(255,255,255,0.4)", px: 4 }}
            onClick={() => {
              socket.emit("leaveRoom");
              localStorage.removeItem("roomCode");
            }}
          >
            Leave Game
          </Button>
        </Box>
      </Box>
    );
  }

  // ===== Shared sections =====

  // Timer bar — shown at top of Players section
  const TimerBar = isMantriGuessPhase && mantriTimer > 0 ? (
    <Box sx={{ px: 2, pt: 1.5, flexShrink: 0 }}>
      <CountdownTimer
        seconds={mantriTimer}
        message={currentRole === "Mantri" ? "Your turn to guess" : "Mantri is guessing..."}
      />
    </Box>
  ) : null;

  const PlayersSection = (
    <Box sx={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
      {/* Timer at top — always visible on Players tab */}
      {TimerBar}

      {roomState.status === "waiting" ? (
        <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", textAlign: "center", p: 3 }}>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <Typography variant="body1" sx={{ opacity: 0.7 }}>
              {roomState.players.length < 4
                ? `Waiting for ${4 - roomState.players.length} more player${4 - roomState.players.length > 1 ? "s" : ""}...`
                : "All players joined! Host can start the game."}
            </Typography>
            {/* Player slots */}
            <Box sx={{ display: "flex", justifyContent: "center", gap: 2, mt: 3 }}>
              {[0, 1, 2, 3].map((i) => {
                const p = roomState.players[i];
                return (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: i * 0.1 }}
                  >
                    <Box sx={{
                      width: 56, height: 56, borderRadius: 2,
                      border: `2px dashed ${p ? theme.palette.primary.main : theme.palette.divider}`,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      backgroundColor: p ? alpha(theme.palette.primary.main, 0.1) : "transparent",
                      transition: "all 0.3s ease",
                    }}>
                      {p ? (
                        <Avatar sx={{ width: 40, height: 40, bgcolor: theme.palette.primary.main, fontSize: "0.9rem", fontWeight: 700 }}>
                          {p.username.charAt(0).toUpperCase()}
                        </Avatar>
                      ) : (
                        <Typography variant="caption" sx={{ opacity: 0.3 }}>?</Typography>
                      )}
                    </Box>
                    {p && <Typography variant="caption" sx={{ display: "block", textAlign: "center", mt: 0.5, fontSize: "0.65rem" }}>{p.username}</Typography>}
                  </motion.div>
                );
              })}
            </Box>
          </motion.div>
        </Box>
      ) : (
        <Box sx={{
          display: "grid",
          gridTemplateColumns: "repeat(2, 1fr)",
          gap: 1.5,
          flex: 1,
          p: 2,
          alignContent: "center",
        }}>
          {roomState.players.map((p, i) => (
            <motion.div
              key={p.id}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: i * 0.08 }}
            >
              <PlayerCard
                player={p}
                isYou={p.id === player.id}
                isRevealed={roundResult?.rolesAssigned?.[p.role] === p.id}
                isGuessed={roundResult?.mantriGuess?.guessedChorId === p.id}
                showGuess={isMantriGuessPhase && roomState.rolesAssigned?.Mantri === player.id && p.id !== player.id && p.connected}
                onGuess={handleMantriGuess}
              />
            </motion.div>
          ))}
        </Box>
      )}

      {/* RoundSummary at bottom of Players section */}
      <RoundSummary
        player={player}
        roundResult={roundResult}
        handleNextRound={handleNextRound}
        handleEndGame={handleEndGame}
        currentRole={currentRole}
        mantriTimer={mantriTimer}
        theme={theme}
        currentRound={roomState.currentRound}
        totalRounds={roomState.totalRounds}
      />

      {/* Start Game + Fill with Bots — waiting state, host only */}
      {!roundResult && roomState.currentRound === 0 && player.isHost && (
        <Box sx={{ p: 2, display: "flex", justifyContent: "center", gap: 1.5, flexWrap: "wrap" }}>
          {roomState.players.length < 4 && (
            <Button
              variant="outlined"
              onClick={() => socket.emit("addBots", { roomCode, count: 4 - roomState.players.length })}
              sx={{ px: 3, py: 1.25, fontWeight: 600, textTransform: "none" }}
            >
              🤖 Fill with Bots
            </Button>
          )}
          <Button
            variant="contained"
            onClick={() => { playClick(); socket.emit("startGame", { roomCode }); }}
            disabled={roomState.players.length < 4}
            sx={{
              px: 4, py: 1.25,
              "&.Mui-disabled": { backgroundColor: theme.palette.action.disabledBackground, color: theme.palette.text.disabled },
            }}
          >
            {roomState.players.length < 4 ? `Need ${4 - roomState.players.length} more` : "Start Game"}
          </Button>
        </Box>
      )}
    </Box>
  );

  const ScoreSection = (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <ScoreboardArea players={roomState.players} currentPlayerId={player.id} />
    </Box>
  );

  const ChatSection = (
    <Box sx={{ flex: 1, display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
      <ChatArea roomCode={roomCode} playerUsername={player.username} socket={socket} messages={roomState.messages || []} />
    </Box>
  );

  // ===== MOBILE: Tabbed layout =====
  if (isMobile) {
    return (
      <Box sx={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0, overflow: "hidden" }}>
        <Tabs
          value={mobileTab}
          onChange={(_, v) => setMobileTab(v)}
          variant="fullWidth"
          sx={{
            minHeight: 40,
            flexShrink: 0,
            backgroundColor: alpha(theme.palette.background.paper, 0.6),
            backdropFilter: "blur(8px)",
            borderBottom: `1px solid ${theme.palette.divider}`,
            "& .MuiTab-root": { minHeight: 40, fontSize: "0.8rem" },
          }}
        >
          <Tab label="Players" />
          <Tab label="Chat" />
          <Tab label="Scores" />
        </Tabs>

        <Box sx={{ flex: 1, minHeight: 0, overflow: "hidden", position: "relative" }}>
          <AnimatePresence mode="wait">
            {mobileTab === 0 && (
              <motion.div
                key="players"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                style={{ position: "absolute", inset: 0, overflowY: "auto", display: "flex", flexDirection: "column" }}
              >
                {PlayersSection}
              </motion.div>
            )}
            {mobileTab === 1 && (
              <motion.div
                key="chat"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}
              >
                {/* VoiceChat compact bar at top of chat on mobile — centered */}
                <Box
                  sx={{
                    px: 2,
                    py: 0.75,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    backgroundColor: alpha(theme.palette.background.paper, 0.6),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 1,
                    flexShrink: 0,
                  }}
                >
                  <VoiceChat socket={socket} roomCode={roomCode} players={roomState.players} compact />
                </Box>
                <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
                  {ChatSection}
                </Box>
              </motion.div>
            )}
            {mobileTab === 2 && (
              <motion.div
                key="scores"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                style={{ position: "absolute", inset: 0, overflowY: "auto" }}
              >
                {ScoreSection}
              </motion.div>
            )}
          </AnimatePresence>
        </Box>
      </Box>
    );
  }

  // ===== DESKTOP: Three-column connected layout =====
  return (
    <Box sx={{ flex: 1, display: "flex", flexDirection: "row", minHeight: 0, overflow: "hidden" }}>
      {/* Scoreboard */}
      <Box sx={{
        width: 280,
        flexShrink: 0,
        borderRight: `1px solid ${theme.palette.divider}`,
        backgroundColor: alpha(theme.palette.background.paper, 0.5),
        backdropFilter: "blur(8px)",
        display: "flex",
        flexDirection: "column",
        overflow: "auto",
      }}>
        {ScoreSection}
      </Box>

      {/* Players (center, largest) */}
      <Box sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        minWidth: 0,
        minHeight: 0,
        overflow: "auto",
      }}>
        {PlayersSection}
      </Box>

      {/* Chat */}
      <Box sx={{
        width: 320,
        flexShrink: 0,
        borderLeft: `1px solid ${theme.palette.divider}`,
        backgroundColor: alpha(theme.palette.background.paper, 0.5),
        backdropFilter: "blur(8px)",
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        height: "100%",
        overflow: "hidden",
      }}>
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
          {ChatSection}
        </Box>
        <Box sx={{ p: 1.5, borderTop: `1px solid ${theme.palette.divider}`, display: "flex", justifyContent: "center" }}>
          <VoiceChat socket={socket} roomCode={roomCode} players={roomState.players} />
        </Box>
      </Box>
    </Box>
  );
}

// ===== Player Card =====
const PlayerCard = ({ player, isYou, isRevealed, isGuessed, showGuess, onGuess }) => {
  const theme = useTheme();
  const isDisconnected = !player.connected;

  const roleColors = {
    Raja: theme.palette.warning.main,
    Mantri: theme.palette.info.main,
    Chor: theme.palette.error.main,
    Sipahi: theme.palette.success.main,
  };

  return (
    <Box
      onClick={() => showGuess && !isDisconnected && onGuess(player.id)}
      className={showGuess && !isDisconnected ? "glow-hover" : ""}
      sx={{
        p: 2,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        cursor: showGuess && !isDisconnected ? "pointer" : "default",
        borderRadius: 2,
        border: isYou
          ? `2px solid ${theme.palette.primary.main}`
          : `1px solid ${theme.palette.divider}`,
        opacity: isDisconnected ? 0.4 : 1,
        filter: isDisconnected ? "grayscale(80%)" : "none",
        position: "relative",
        backgroundColor: isRevealed
          ? alpha(roleColors[player.role] || theme.palette.success.main, 0.12)
          : alpha(theme.palette.background.paper, 0.6),
        backdropFilter: "blur(4px)",
        transition: "all 0.25s ease",
        minHeight: 120,
      }}
    >
      {isDisconnected && (
        <Typography
          variant="caption"
          sx={{ position: "absolute", top: 6, fontWeight: 700, color: "error.main", fontSize: "0.6rem" }}
        >
          OFFLINE
        </Typography>
      )}

      <Box sx={{ position: "relative" }}>
        <Avatar
          sx={{
            width: 52, height: 52, mb: 1, fontSize: "1.2rem", fontWeight: 700,
            bgcolor: isRevealed ? roleColors[player.role] : theme.palette.primary.main,
            color: "#fff",
            boxShadow: isYou ? `0 0 0 3px ${alpha(theme.palette.primary.main, 0.3)}` : "none",
          }}
        >
          {player.username.charAt(0).toUpperCase()}
        </Avatar>
        <FiberManualRecordIcon
          sx={{
            position: "absolute", bottom: 8, right: -2,
            fontSize: 10,
            color: player.connected ? theme.palette.success.main : theme.palette.error.main,
          }}
        />
      </Box>

      <Typography variant="body2" sx={{ fontWeight: 600, textAlign: "center", lineHeight: 1.2 }}>
        {player.username}
        {isYou && <Box component="span" sx={{ opacity: 0.5, ml: 0.5, fontSize: "0.7rem" }}>(You)</Box>}
        {player.isBot && (
          <Box
            component="span"
            sx={{
              ml: 0.5,
              px: 0.6,
              py: 0.1,
              borderRadius: 0.5,
              fontSize: "0.55rem",
              fontWeight: 800,
              backgroundColor: alpha(theme.palette.info.main, 0.15),
              color: theme.palette.info.main,
              letterSpacing: 0.5,
            }}
          >
            BOT
          </Box>
        )}
      </Typography>

      <AnimatePresence>
        {isRevealed && (
          <motion.div initial={{ opacity: 0, scale: 0.5 }} animate={{ opacity: 1, scale: 1 }} transition={{ type: "spring", stiffness: 300 }}>
            <Chip
              label={player.role}
              size="small"
              sx={{
                mt: 1, fontWeight: 700, fontSize: "0.7rem",
                backgroundColor: roleColors[player.role],
                color: "#fff",
              }}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </Box>
  );
};

// ===== Round Summary =====
const RoundSummary = ({ roundResult, player, handleNextRound, handleEndGame, currentRole, mantriTimer, theme, currentRound, totalRounds }) => {
  const isLastRound = currentRound > 0 && currentRound >= totalRounds;

  if (!roundResult) return null;

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
      <Box sx={{ py: 1.5, px: 2 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, textAlign: "center", mb: 1 }}>
          Round Summary
        </Typography>
        <Box sx={{ backgroundColor: alpha(theme.palette.background.default, 0.5), borderRadius: 1.5, p: 1.5, mb: 1.5, fontSize: "0.85rem" }}>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            <strong>Chor:</strong> {roundResult.chorUsername}
          </Typography>
          <Typography variant="body2">
            <strong>Mantri's guess:</strong>{" "}
            {roundResult.mantriGuess?.hasGuessed
              ? roundResult.mantriGuess.isCorrect
                ? <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, color: "success.main" }}>Correct <CheckCircleIcon sx={{ fontSize: 16 }} /></Box>
                : <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, color: "error.main" }}>Incorrect <CancelIcon sx={{ fontSize: 16 }} /></Box>
              : "No guess made"}
          </Typography>
        </Box>
        {player?.isHost && (
          <Box sx={{ display: "flex", gap: 1.5, justifyContent: "center" }}>
            <Button
              variant="contained"
              size="small"
              onClick={handleNextRound}
              sx={{ px: 2.5 }}
            >
              {isLastRound ? "See Results" : "Next Round"}
            </Button>
            {!isLastRound && (
              <Button variant="outlined" size="small" color="error" onClick={handleEndGame} sx={{ px: 2.5 }}>
                End Game
              </Button>
            )}
          </Box>
        )}
      </Box>
    </motion.div>
  );
};

export default GameArea;
